import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TeeSectionHeader from '@/components/ui/tee-section-header';
import TeeSectionFooter from '@/components/ui/tee-section-footer';
import { TeeSectionTitle } from '@/components/ui/tee-section-title';
import { TeeInput } from '@/components/ui/tee-input';
import { TeeTextArea } from '@/components/ui/tee-textarea';
import { TeeLoader } from '@/components/ui/tee-loader';
import { TeeToggle } from '@/components/ui/tee-toggle';
import TeeBadge from '@/components/ui/tee-badge';
import { showSuccessToast, showErrorToast } from '@/utils/toast.utils';
import { getErrorMessage } from '@/utils/error';
import { userProfileService } from '@/services/user-profile.service';
import { syncCachedUser } from '@/utils/user-cache';
import type { User } from '@/types/user';
import { ProfilePhotoEditor } from './ProfilePhotoEditor';

/** Only what the API accepts on PUT /user-profile. */
type ProfileForm = {
  firstName: string;
  lastName: string;
  mobile: string;
  tag: string;
  bio: string;
  experience: string;
  certifications: string;
  facebookUrl: string;
  instagramUrl: string;
};

const EMPTY_FORM: ProfileForm = {
  firstName: '',
  lastName: '',
  mobile: '',
  tag: '',
  bio: '',
  experience: '',
  certifications: '',
  facebookUrl: '',
  instagramUrl: '',
};

function toForm(user: User): ProfileForm {
  return {
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    mobile: user.mobile ?? '',
    tag: user.tag ?? '',
    bio: user.bio ?? '',
    experience: user.experience ?? '',
    certifications: user.certifications ?? '',
    facebookUrl: user.facebookUrl ?? '',
    instagramUrl: user.instagramUrl ?? '',
  };
}

export default function ProfilePage() {
  const { t } = useTranslation();

  const [profile, setProfile] = useState<User | null>(null);
  const [form, setForm] = useState<ProfileForm>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [removingPhoto, setRemovingPhoto] = useState(false);

  useEffect(() => {
    let cancelled = false;
    userProfileService
      .getProfile()
      .then((user) => {
        if (cancelled) return;
        setProfile(user);
        setForm(toForm(user));
      })
      .catch((err) => showErrorToast(getErrorMessage(err, t('profile.loadError'))))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const set = <K extends keyof ProfileForm>(field: K, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const applyUpdate = (user: User) => {
    setProfile(user);
    setForm(toForm(user));
    syncCachedUser(user);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      applyUpdate(await userProfileService.updateProfile(form));
      showSuccessToast(t('profile.saveSuccess'));
    } catch (err) {
      showErrorToast(getErrorMessage(err, t('profile.saveError')));
    } finally {
      setSaving(false);
    }
  };

  /** NC-2: Minor/Info issue emails can be switched off; saved immediately. */
  const handleIssueEmail = async (severity: 'minor' | 'info', enabled: boolean) => {
    const current = (profile?.issueNoticeEmailOptOuts ?? []) as ('minor' | 'info')[];
    const next = enabled ? current.filter((s) => s !== severity) : [...new Set([...current, severity])];
    try {
      const timezone = profile?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
      applyUpdate(await userProfileService.updateProfile({ issueNoticeEmailOptOuts: next, timezone }));
      showSuccessToast(t('profile.saveSuccess'));
    } catch (err) {
      showErrorToast(getErrorMessage(err, t('profile.saveError')));
    }
  };

  const handleUploadPhoto = async (file: File) => {
    setUploadingPhoto(true);
    try {
      applyUpdate(await userProfileService.uploadPhoto(file));
      showSuccessToast(t('profile.photoUpdated'));
    } catch (err) {
      showErrorToast(getErrorMessage(err, t('profile.photoError')));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    setRemovingPhoto(true);
    try {
      applyUpdate(await userProfileService.removePhoto());
      showSuccessToast(t('profile.photoRemoved'));
    } catch (err) {
      showErrorToast(getErrorMessage(err, t('profile.photoError')));
    } finally {
      setRemovingPhoto(false);
    }
  };

  if (loading) {
    return (
      <div className="section_container">
        <TeeLoader />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="section_container">
        <div className="flex flex-col items-center justify-center py-16 gap-2 text-center">
          <p className="text-base font-semibold text-[var(--text-content-title)]">
            {t('profile.loadError')}
          </p>
        </div>
      </div>
    );
  }

  const initials =
    (profile.firstName?.charAt(0) ?? '') + (profile.lastName?.charAt(0) ?? '');
  const isDirty = JSON.stringify(form) !== JSON.stringify(toForm(profile));

  return (
    <div className="section_container">
      <TeeSectionHeader title={t('profile.title')} description={t('profile.description')} />

      <div className="section_body">
        <TeeSectionTitle title={t('profile.photoTitle')} />
        <ProfilePhotoEditor
          photoUrl={profile.profilePhoto}
          initials={initials}
          isUploading={uploadingPhoto}
          isRemoving={removingPhoto}
          onUpload={handleUploadPhoto}
          onRemove={handleRemovePhoto}
        />

        <TeeSectionTitle title={t('profile.detailsTitle')} />
        <div className="flex flex-col items-stretch gap-4 md:flex-row md:gap-6">
          <TeeInput
            label={t('profile.firstName')}
            value={form.firstName}
            onChange={(e) => set('firstName', e.target.value)}
            disabled={saving}
          />
          <TeeInput
            label={t('profile.lastName')}
            value={form.lastName}
            onChange={(e) => set('lastName', e.target.value)}
            disabled={saving}
          />
        </div>

        <div className="flex flex-col items-stretch gap-4 md:flex-row md:gap-6">
          <TeeInput
            label={t('profile.mobile')}
            value={form.mobile}
            onChange={(e) => set('mobile', e.target.value)}
            disabled={saving}
          />
          <TeeInput
            label={t('profile.tag')}
            value={form.tag}
            onChange={(e) => set('tag', e.target.value)}
            hint={t('profile.tagHint')}
            disabled={saving}
          />
        </div>

        {/* Read-only identity. Email is the login identifier and role/location
            are authorisation inputs — both are changed by an administrator, not
            here, so they are shown for reference rather than edited. */}
        <div className="flex flex-col gap-2">
          <span className="text-body-s font-medium text-[var(--text-content-title)]">
            {t('profile.accountTitle')}
          </span>
          <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-3">
            <span className="text-body-s text-[var(--text-content-sub-title)]">
              {profile.email}
            </span>
            {profile.roleName && <TeeBadge type="grey" size="s" label={profile.roleName} />}
          </div>
          <p className="text-body-xs text-[var(--text-content-sub-title)]">
            {t('profile.accountHint')}
          </p>
        </div>

        <TeeSectionTitle title={t('profile.aboutTitle')} />
        <TeeTextArea
          label={t('profile.bio')}
          rows={4}
          value={form.bio}
          onChange={(e) => set('bio', e.target.value)}
          disabled={saving}
        />
        <TeeTextArea
          label={t('profile.experience')}
          rows={3}
          value={form.experience}
          onChange={(e) => set('experience', e.target.value)}
          disabled={saving}
        />
        <TeeTextArea
          label={t('profile.certifications')}
          rows={3}
          value={form.certifications}
          onChange={(e) => set('certifications', e.target.value)}
          disabled={saving}
        />

        <div className="flex flex-col items-stretch gap-4 md:flex-row md:gap-6">
          <TeeInput
            label={t('profile.facebookUrl')}
            value={form.facebookUrl}
            onChange={(e) => set('facebookUrl', e.target.value)}
            disabled={saving}
          />
          <TeeInput
            label={t('profile.instagramUrl')}
            value={form.instagramUrl}
            onChange={(e) => set('instagramUrl', e.target.value)}
            disabled={saving}
          />
        </div>

        <TeeSectionTitle title={t('issues.profile.emailTitle')} />
        <p className="text-body-xs text-[var(--text-content-sub-title)]">{t('issues.profile.emailHint')}</p>
        <TeeToggle
          label={t('issues.profile.minor')}
          checked={!(profile.issueNoticeEmailOptOuts ?? []).includes('minor')}
          onChange={(v) => handleIssueEmail('minor', v)}
        />
        <TeeToggle
          label={t('issues.profile.info')}
          checked={!(profile.issueNoticeEmailOptOuts ?? []).includes('info')}
          onChange={(v) => handleIssueEmail('info', v)}
        />
      </div>

      <TeeSectionFooter
        actionLabel={saving ? t('common.saving') : t('common.save')}
        onAction={handleSave}
        showActionIcon={false}
        secondaryActionLabel={t('common.cancel')}
        onSecondaryAction={() => setForm(toForm(profile))}
        actionDisabled={saving || !isDirty}
      />
    </div>
  );
}
